import { ACCESS_LEVELS, ACCESS_LEVELS_DICT, ACCESS_LEVELS_GROUPS } from '@/lib/Access.js'
import DataFlowIcon from '@/assets/untitled-ui/dataflow-04.svg'
import ListItemIcon from '@/assets/untitled-ui/list.svg'
import ChartBreakout from '@/assets/untitled-ui/chart-breakout-square.svg'
import AnalyticsIcon from '@/assets/untitled-ui/bar-chart-10.svg'
import KanbanIcon from '@/assets/untitled-ui/columns-01.svg'
import { FEATURES } from '@/lib/Features'

//legacy, this will come from access control
export const FeatureAccess = {
  [FEATURES.OrgChart]: ACCESS_LEVELS_GROUPS.ALL,
  [FEATURES.Directory]: ACCESS_LEVELS_GROUPS.ABOVE_IC,
  [FEATURES.Forecast]: ACCESS_LEVELS_GROUPS.TOP_LEVEL_FINANCE,
  [FEATURES.Scenarios]: ACCESS_LEVELS_GROUPS.ABOVE_IC,
  [FEATURES.AccessControl]: [ACCESS_LEVELS_DICT.ADMIN],
  [FEATURES.Hub]: ACCESS_LEVELS_GROUPS.TOP_LEVEL_FINANCE_HR,
  [FEATURES.Settings]: [ACCESS_LEVELS_DICT.ADMIN],
  [FEATURES.DataManagement]: [ACCESS_LEVELS_DICT.ADMIN],
  [FEATURES.ExportData]: [ACCESS_LEVELS_DICT.ADMIN],
  [FEATURES.Productivity]: [ACCESS_LEVELS_DICT.ADMIN]
}

export const resolveAccessToFeatureNoFallback = ({ feature, accessScopesFeatures }) => {
  if (accessScopesFeatures && Object.keys(accessScopesFeatures).includes(feature)) {
    return accessScopesFeatures[feature]
  }

  return false
}

/**
 * Resolve access to feature based on access scopes and and legacy role mapping
 * accessScopesFeatures will take precedence over FeatureAccess
 */
export const resolveAccessToFeature = ({ feature, accessScopesFeatures, role }) => {
  //check if feature is in accessScopesFeatures keys
  if (accessScopesFeatures && Object.keys(accessScopesFeatures).includes(feature)) {
    return accessScopesFeatures[feature]
  }

  //check if role is among legacy pre-defined roles
  if (!ACCESS_LEVELS.includes(role)) {
    //to not lock out users with custom access groups
    //their permissions will be "open" as they are currently in pre-release of feature controls state
    //example: for a custom group of "HRBP" we won't have any record of feature access config in DB or in the legacy access control groups
    //that group would have access to all features by default before feature level access scopes were deployed

    return true
  }

  //return legacy role mapping
  return FeatureAccess[feature].includes(role)
}

/**
 * Get configuration for navigation bar
 *
 * TODO: bind url to router/index.js as main source of truth
 *
 * @param boardId - main board id
 * @param route
 * @param isVerified
 * @param role
 * @param features
 * @param isEmbedded - whether the app is running in embedded mode (Dayforce)
 * @returns {Object}
 */
export const getNavigationConfig = ({ boardId, route, isVerified, role, features, isEmbedded }) => {
  // In embedded mode (Dayforce SSO), users often don't have emailVerified set to true
  // so we skip the isVerified check when embedded
  const canShowNavigation = boardId && (isVerified || isEmbedded) && route.name !== 'No Access'

  if (canShowNavigation) {
    return {
      [FEATURES.Scenarios]: {
        name: 'All scenarios',
        url: `/plans/${boardId}`,
        show: resolveAccessToFeature({
          feature: FEATURES.Scenarios,
          accessScopesFeatures: features,
          role
        })
      },
      [FEATURES.OrgChart]: {
        name: FEATURES.OrgChart,
        label: 'Org Chart',
        icon: DataFlowIcon,
        url: `/board/${boardId}`,
        keyboardShortcut: 1,
        show: resolveAccessToFeature({
          feature: FEATURES.OrgChart,
          accessScopesFeatures: features,
          role
        })
      },
      [FEATURES.Directory]: {
        name: FEATURES.Directory,
        icon: ListItemIcon,
        label: 'Directory',
        keyboardShortcut: 2,
        url: `/table/${boardId}`,
        show: resolveAccessToFeature({
          feature: FEATURES.Directory,
          accessScopesFeatures: features,
          role
        })
      },
      [FEATURES.Forecast]: {
        name: FEATURES.Forecast,
        icon: ChartBreakout,
        label: 'Forecast',
        keyboardShortcut: 3,
        url: `/forecast/${boardId}`,
        show: resolveAccessToFeature({
          feature: FEATURES.Forecast,
          accessScopesFeatures: features,
          role
        })
      },
      [FEATURES.AccessControl]: {
        name: 'Access control',
        url: `/invite-access/${boardId}`,
        show: resolveAccessToFeature({
          feature: FEATURES.AccessControl,
          accessScopesFeatures: features,
          role
        })
      },
      [FEATURES.Hub]: {
        name: FEATURES.Hub,
        icon: AnalyticsIcon,
        label: 'Workforce Hub',
        url: `/hub/${boardId}`,
        keyboardShortcut: 4,
        show: resolveAccessToFeature({
          feature: FEATURES.Hub,
          accessScopesFeatures: features,
          role
        })
      },
      [FEATURES.Productivity]: {
        name: FEATURES.Productivity,
        icon: KanbanIcon,
        label: 'Productivity',
        url: `/productivity/${boardId}`,
        keyboardShortcut: 5,
        show: true
      },
      [FEATURES.Settings]: {
        name: 'General settings',
        url: `/settings/${boardId}`,
        show: resolveAccessToFeature({
          feature: FEATURES.Settings,
          accessScopesFeatures: features,
          role
        })
      }
    }
  }

  return {}
}
